import React, { useState, useEffect } from 'react';
import { getPendingKyc, getMerchantKycDocs, approveRejectKyc } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { showSuccessToast, showErrorToast } from '../../utils/toast';

const KycRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [docs, setDocs] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = async () => {
    try {
      const res = await getPendingKyc();
      setRequests(res.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const viewDocs = async (merchant) => {
    setSelectedMerchant(merchant);
    try {
      const res = await getMerchantKycDocs(merchant.merchantId);
      setDocs(res.data || []);
    } catch (err) { showErrorToast('Failed to load documents'); }
  };

  const handleApproval = async (approved) => {
    setProcessing(true);
    try {
      await approveRejectKyc(selectedMerchant.merchantId, { approved, remarks });
      showSuccessToast(approved ? 'KYC approved' : 'KYC rejected');
      setSelectedMerchant(null);
      loadRequests();
    } catch (err) { showErrorToast('Failed to process'); } finally { setProcessing(false); }
  };

  if (loading) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">KYC Requests</h1>
      {requests.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-gray-500">No pending KYC requests</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {requests.map((m) => (
            <Card key={m.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{m.merchantName}</p>
                  <p className="text-sm text-gray-500">{m.merchantId} • {m.email}</p>
                </div>
                <Button onClick={() => viewDocs(m)}>View Documents</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selectedMerchant} onOpenChange={() => setSelectedMerchant(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>KYC Documents - {selectedMerchant?.merchantName}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {docs.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">{doc.documentType.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-gray-500">{doc.fileName}</p>
                </div>
                <Badge className={doc.isVerified ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                  {doc.isVerified ? 'Verified' : 'Pending'}
                </Badge>
              </div>
            ))}
            {docs.length === 0 && <p className="text-gray-500 text-center py-4">No documents uploaded</p>}
            <Textarea placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => handleApproval(true)} disabled={processing}>Approve</Button>
              <Button variant="destructive" className="flex-1" onClick={() => handleApproval(false)} disabled={processing}>Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KycRequests;
